import { useQuery } from "@tanstack/react-query";
import { Grid3x3 } from "lucide-react";
import { fetchCorrelation } from "@/lib/api";

const ABBR = { "DXY": "DXY", "S&P 500": "SPX", "Gold": "GLD", "UST 10Y": "10Y", "WTI": "WTI", "VIX": "VIX" };

const cellBg = (c) => {
  if (c == null) return "transparent";
  if (c >= 0.999) return "rgba(245,158,11,0.22)"; // diagonal
  const a = Math.min(0.5, Math.abs(c) * 0.5);
  return c >= 0 ? `rgba(var(--heat-pos-rgb),${a})` : `rgba(239,68,68,${a})`;
};

export default function CrossAssetMatrix() {
  const q = useQuery({
    queryKey: ["cross-correlation"],
    queryFn: fetchCorrelation,
    staleTime: 1000 * 60 * 60 * 3,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="cross-asset-matrix-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Grid3x3 size={11} strokeWidth={1.5} className="text-amber-term" /> Cross-Asset Correlation
        </span>
        <span className={`text-[11px] uppercase tracking-widest ${d?.status === "insufficient_data" ? "text-amber-term" : "text-zinc-600"}`}>
          {d?.status === "insufficient_data" ? "limited data" : d ? `${d.window}D daily returns` : "daily returns"}
        </span>
      </div>

      {q.isLoading ? (
        <div className="h-[180px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Computing matrix…</div>
      ) : q.isError || !d?.labels?.length ? (
        <div className="h-[180px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">Correlation unavailable</div>
      ) : (
        <div className="p-3 overflow-x-auto">
          <table className="w-full text-[11px] tabular-nums" data-testid="corr-matrix">
            <thead>
              <tr>
                <th className="p-1" />
                {d.labels.map((l) => (
                  <th key={l} className="p-1 text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{ABBR[l] || l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.matrix.map((row, i) => (
                <tr key={d.labels[i]}>
                  <td className="p-1 pr-2 text-[11px] uppercase tracking-widest text-zinc-500 text-right whitespace-nowrap">{ABBR[d.labels[i]] || d.labels[i]}</td>
                  {row.map((c, j) => (
                    <td key={j} className="p-1 text-center">
                      <div
                        className="h-7 flex items-center justify-center rounded-[2px] text-zinc-100"
                        style={{ backgroundColor: cellBg(c) }}
                      >
                        {c == null ? "·" : c.toFixed(2)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-3 mt-2 text-[11px] uppercase tracking-widest text-zinc-600">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3" style={{ background: "rgba(var(--heat-pos-rgb),0.5)" }} /> +corr</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3" style={{ background: "rgba(239,68,68,0.5)" }} /> −corr</span>
          </div>
        </div>
      )}
    </div>
  );
}
